<script>
import { termsStore } from '../../stores/termsStore.js';
import { slide } from 'svelte/transition';
import { onDestroy } from 'svelte';

// View filter menu state
let showViewMenu = false;
$: currentViewFilter = $termsStore.viewFilter || 'all';
$: selectedEntries = $termsStore.selectedEntries || new Set();
$: focusedClaimNumber = $termsStore.focusedClaimNumber;

// Get filter display name
function getFilterDisplayName(filter) {
    switch (filter) {
        case 'errors': return 'Errors';
        case 'selected': return 'Selected';
        case 'focus': return 'Focus';
        case 'all': 
        default: return 'All';
    }
}

// Get filter icon
function getFilterIcon(filter) {
    switch (filter) {
        case 'errors': return 'fa-exclamation-triangle';
        case 'selected': return 'fa-check-square';
        case 'focus': return 'fa-crosshairs';
        case 'all': 
        default: return 'fa-list';
    }
}

// Handle filter button click
function handleFilterButtonClick(e) {
    e.stopPropagation();
    showViewMenu = !showViewMenu;
}

// Handle filter selection
function handleFilterSelect(filter) {
    console.log(`[ViewFilterDropdown] Setting view filter to: ${filter}`);
    termsStore.setViewFilter(filter);
    showViewMenu = false;
}

// Handle clear all selected terms
function handleClearAll() {
    console.log(`[ViewFilterDropdown] Clearing all selected terms`);
    termsStore.clearAllSelections();
    showViewMenu = false;
}

// Handle click outside
function handleClickOutside(event) {
    if (!event.target.closest('.view-filter-menu')) {
        showViewMenu = false;
    }
}

// Handle keyboard
function handleKeydown(e) {
    if (e.key === 'Escape') {
        showViewMenu = false;
    }
}

// Close menu on component destroy
onDestroy(() => {
    showViewMenu = false;
});

// Check if filters are applicable
$: hasSelectedTerms = selectedEntries.size > 0;
$: hasFocusedClaim = focusedClaimNumber !== null;
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleKeydown} />

<div class="view-filter-menu">
    <button 
        class="toolbar-button terms-button {currentViewFilter !== 'all' ? 'active' : ''}"
        on:click={handleFilterButtonClick}
        title="Filter terms view"
        aria-expanded={showViewMenu}
        aria-haspopup="true"
        data-testid="view-filter-button"
    >
        <i class="fas fa-filter"></i>
        <span class="button-text">
            {#if currentViewFilter !== 'all'}
                View: {getFilterDisplayName(currentViewFilter)}
            {:else}
                View
            {/if}
        </span>
    </button>
    
    {#if showViewMenu}
        <div 
            class="filter-dropdown" 
            transition:slide|local={{ duration: 200 }}
            role="menu"
            tabindex="-1"
        >
            <button 
                class="menu-item {currentViewFilter === 'all' ? 'active' : ''}" 
                on:click={() => handleFilterSelect('all')}
                role="menuitem"
                aria-current={currentViewFilter === 'all' ? 'true' : 'false'}
                data-testid="filter-all-button"
            >
                Show all terms
                {#if currentViewFilter === 'all'}
                    <i class="fas fa-check menu-item-check"></i>
                {/if}
            </button>
            
            <button 
                class="menu-item {currentViewFilter === 'errors' ? 'active' : ''}" 
                on:click={() => handleFilterSelect('errors')}
                role="menuitem"
                aria-current={currentViewFilter === 'errors' ? 'true' : 'false'}
                data-testid="filter-errors-button"
            >
                Show terms with errors
                {#if currentViewFilter === 'errors'}
                    <i class="fas fa-check menu-item-check"></i>
                {/if}
            </button>
            
            
            <button 
                class="menu-item {currentViewFilter === 'selected' ? 'active' : ''} {!hasSelectedTerms ? 'disabled' : ''}" 
                on:click={() => hasSelectedTerms && handleFilterSelect('selected')}
                role="menuitem"
                aria-current={currentViewFilter === 'selected' ? 'true' : 'false'}
                disabled={!hasSelectedTerms}
                title={!hasSelectedTerms ? 'No terms selected' : ''}
                data-testid="filter-selected-button"
            >
                Show selected only
                {#if selectedEntries.size > 0}
                    <span class="count-badge">{selectedEntries.size}</span>
                {/if}
                {#if currentViewFilter === 'selected'}
                    <i class="fas fa-check menu-item-check"></i>
                {/if}
            </button>
            
            <button 
                class="menu-item {currentViewFilter === 'focus' ? 'active' : ''} {!hasFocusedClaim ? 'disabled' : ''}" 
                on:click={() => hasFocusedClaim && handleFilterSelect('focus')}
                role="menuitem"
                aria-current={currentViewFilter === 'focus' ? 'true' : 'false'}
                disabled={!hasFocusedClaim}
                title={!hasFocusedClaim ? 'Click on a claim first' : `Showing terms from claim ${focusedClaimNumber}`}
                data-testid="filter-focus-button"
            >
                Focus on claim:
                {#if focusedClaimNumber}
                    <span class="claim-badge">{focusedClaimNumber}</span>
                {/if}
                {#if currentViewFilter === 'focus'}
                    <i class="fas fa-check menu-item-check"></i>
                {/if}
            </button>
            
            <div class="menu-divider"></div>
            
            <button 
                class="menu-item {selectedEntries.size === 0 ? 'disabled' : ''}" 
                on:click={() => selectedEntries.size > 0 && handleClearAll()}
                role="menuitem"
                disabled={selectedEntries.size === 0}
                title={selectedEntries.size === 0 ? 'No terms selected' : `Clear ${selectedEntries.size} selected terms`}
                data-testid="clear-all-button"
            >
                Clear all selected terms
                {#if selectedEntries.size > 0}
                    <span class="count-badge">{selectedEntries.size}</span>
                {/if}
            </button>
        </div>
    {/if}
</div>

<style>
.view-filter-menu {
    position: relative;
}

.toolbar-button.terms-button {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
    padding: 0 var(--spacing-3);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background-color: var(--color-btn-terms);
    border: none;
    border-radius: var(--border-radius-xl);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: 6px;
}

.toolbar-button.terms-button:hover {
    background-color: var(--color-btn-terms-hover);
    box-shadow: var(--shadow-sm);
    transform: translateY(-1px);
}

.toolbar-button.terms-button.active {
    color: var(--color-text-button);
    background-color: var(--color-terms-500);
    box-shadow: var(--shadow-terms-button-active);
}


.filter-dropdown {
    position: absolute;
    top: calc(100% + var(--spacing-1));
    left: 50%;
    z-index: var(--z-index-dropdown);
    min-width: 200px;
    padding: var(--spacing-2);
    background-color: var(--color-bg-terms);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-dropdown);
    transform: translateX(-50%);
}

.menu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--spacing-2) var(--spacing-3);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-radius: var(--border-radius-md);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: var(--spacing-2);
}

.menu-item:hover:not(.disabled) {
    background-color: var(--color-bg-hover-light);
}

.menu-item.active {
    background-color: var(--color-bg-hover-medium);
}

.menu-item.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.menu-item-check {
    margin-left: auto;
    color: var(--color-success);
}

.menu-divider {
    height: 1px;
    margin: var(--spacing-1) 0;
    background-color: var(--color-terms-divider);
}

.count-badge, .claim-badge {
    padding: 1px 6px;
    margin-left: auto;
    font-size: 11px;
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-inverse);
    background-color: var(--color-terms-600);
    border-radius: 10px;
}

.claim-badge {
    background-color: var(--color-info);
}

/* For very small viewports, match compact styling */
@media (width <= 1300px) {
    .toolbar-button.terms-button {
        height: 28px;
        padding: 0 var(--spacing-2);
        font-size: var(--font-size-xs);
    }
    
    .toolbar-button.terms-button i {
        font-size: 14px;
    }
}
</style>